import { Exception } from '@/redux/slices/slice_types';
import { Divider, Drawer, DrawerProps, Grid, IconButton, List, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { LabelValue } from '../_common';
import { useEventListener } from '../_hooks/useEventListener';
import PolarisIcon from '../_common/PolarisIcon';
import styled from '@emotion/styled';
import Refresh from '../_common/Refresh';
import { convertStylesToStyled } from '@/_utils/utils';
import ExceptionEnumMap from './ExceptionEnumMap';
import ExceptionSeverity from './ExceptionSeverity';
import { TableStatus } from '../_common/Table';
import AssigneeSelect from '@/components/_common/AssigneeSelect/AssigneeSelect';
import History from '@/components/_common/History/History';
import { findandFormatUTCDateInString } from '@/_utils/date';
import DrawerLinkActionButton from '@/components/Exceptions/ActionButtons/DrawerLinkActionButton';
import DrawerApiActionButton from '@/components/Exceptions/ActionButtons/DrawerApiActionButton';
import { useRefreshActiveException, useRefreshExceptions } from '@/components/Exceptions/hooks/refreshExceptions.hook';
import ExceptionReferences from '@/components/Exceptions/References/ExceptionReferences';
import { LabelValueDefaultProps } from '@/components/Exceptions/types';
import DateTimeDisplay from '@/components/_common/DateTimeDisplay';
import Notes from '@/components/_common/Notes/Notes';
import ClickCopy from '@/components/_common/ClickCopy/ClickCopy';
import ExceptionResolve from '@/components/Exceptions/ExceptionResolve';

type ExceptionDrawerProps = {
  exception: Exception | null;
  onDrawerClose: () => void;
  popoutOnly?: boolean;
  loading?: boolean;
};

const DRAWER_ANIMATION_DURATION = 250;

const ExceptionDrawer = ({ exception, onDrawerClose, popoutOnly = false, loading = false }: ExceptionDrawerProps) => {
  const [anchor, setAnchor] = useState<DrawerProps['anchor']>('right');
  const [open, setOpen] = useState(false);
  const LabelValueProps: LabelValueDefaultProps = { color: 'primary' };

  useEffect(() => {
    setOpen(popoutOnly || !!exception?.id);
  }, [popoutOnly, exception]);

  const onCloseConfirm = () => {
    let timeout = 0;

    if (!popoutOnly) {
      setOpen(false);
      timeout = DRAWER_ANIMATION_DURATION;

      setTimeout(() => {
        onDrawerClose();
      }, timeout);
    } else {
      window.close();
    }
  };

  const handlePopOut = () => {
    onDrawerClose();
    if (!exception) return;
    window.open(`/exceptions/?id=${exception.id}&popout=1`, 'popout', `popup=1,height=600,width=400,top=0,left=-${window.screen.width}`);
  };

  const handler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (popoutOnly) {
        window.close();
      } else {
        onClose();
      }
    }
  };

  useEventListener('keydown', handler);

  const onClose = () => onCloseConfirm();

  const refreshExceptions = useRefreshExceptions();
  const refreshActiveException = useRefreshActiveException();

  if (!exception) return null;

  return (
    <FormattedDrawer
      variant="persistent"
      anchor={anchor}
      open={open}
      onClose={onClose}
      popout={`${popoutOnly}`}
      hideBackdrop
      transitionDuration={{ enter: popoutOnly ? 0 : DRAWER_ANIMATION_DURATION, exit: DRAWER_ANIMATION_DURATION }}
    >
      {!popoutOnly && (
        <>
          <ActionBar container>
            <Tooltip title={`Dock to ${anchor === 'right' ? 'left' : 'right'}`}>
              <IconButton onClick={() => setAnchor(anchor === 'right' ? 'left' : 'right')}>
                <PolarisIcon icon={anchor === 'right' ? 'dock_left' : 'dock_right'} height={20} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Pop out">
              <IconButton onClick={handlePopOut}>
                <PolarisIcon icon="popup" height={20} />
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose as any} sx={{ ml: 'auto' }}>
              <PolarisIcon icon="close" />
            </IconButton>
          </ActionBar>
          <Divider sx={{ mx: 2 }} />
        </>
      )}

      <Grid
        container
        alignItems="flex-start"
        sx={{
          p: 3.5,
          pt: 0,
          opacity: loading ? 0.4 : 1,
          pointerEvents: loading ? 'none' : 'auto'
        }}
      >
        <Grid sx={{ pt: 1, pb: 3, ml: -2 }} container alignItems="center" justifyContent="space-between">
          <RefreshButton onClick={refreshExceptions} color="inherit" />
        </Grid>
        <Grid container alignItems="flex-start" gap={4}>
          <LabelValue label="ID" sx={{ whiteSpace: 'nowrap' }} {...LabelValueProps}>
            <strong>{exception.hid}</strong>
          </LabelValue>
          <LabelValue label="Type" sx={{ whiteSpace: 'nowrap' }} {...LabelValueProps}>
            <ExceptionEnumMap enumRef="type" value={exception.type} />
          </LabelValue>
          <LabelValue label="Subtype" sx={{ whiteSpace: 'nowrap' }} {...LabelValueProps}>
            <ExceptionEnumMap enumRef="subtype" value={exception.subtype} />
          </LabelValue>
          <LabelValue label="Severity" sx={{ whiteSpace: 'nowrap' }} {...LabelValueProps}>
            {exception.severity ? <ExceptionSeverity severity={exception.severity} /> : '-'}
          </LabelValue>
          <LabelValue label="Status" sx={{ whiteSpace: 'nowrap' }} {...LabelValueProps}>
            <TableStatus status={exception.status.toLowerCase()} statusReason={exception.status_reason} />
          </LabelValue>
          <LabelValue label="Assignee" sx={{ maxWidth: 195 }}>
            <AssigneeSelect
              update="exception"
              value={exception.assigned_user}
              id={exception.id}
              assigns="user"
              row={exception}
              userDisplayIconStyle={{ marginRight: 8 }}
              assignButtonSx={{ px: 2 }}
              onChange={refreshActiveException}
            />
          </LabelValue>
          <LabelValue label="Description" size={{ xs: 12 }} {...LabelValueProps}>
            {exception.description ? findandFormatUTCDateInString(exception.description, true) : '-'}
          </LabelValue>
          <LabelValue label="Created at" {...LabelValueProps}>
            <DateTimeDisplay>{exception.created_at}</DateTimeDisplay>
          </LabelValue>
          <LabelValue label="Updated at" {...LabelValueProps}>
            <DateTimeDisplay>{exception.updated_at}</DateTimeDisplay>
          </LabelValue>
          <LabelValue label="Notes" {...LabelValueProps}>
            <Notes data={exception} />
          </LabelValue>
          <LabelValue label="History" {...LabelValueProps}>
            <History iconOnly data={exception} />
          </LabelValue>
        </Grid>
        {(exception.stack_trace || exception.error_body) && (
          <>
            <FullWidthDivider />
            <SectionHeader variant="h6">Error Info</SectionHeader>
          </>
        )}
        {exception.error_body && (
          <LabelValue size={{ xs: 12 }} sx={{ mb: 2 }} label="Error" {...LabelValueProps}>
            <ClickCopy copyText={exception.error_body}>
              <ScrollableTextArea>{exception.error_body}</ScrollableTextArea>
            </ClickCopy>
          </LabelValue>
        )}
        {exception.stack_trace && (
          <LabelValue size={{ xs: 12 }} label="Stack Trace" {...LabelValueProps}>
            <ClickCopy copyText={exception.stack_trace}>
              <ScrollableTextArea>{exception.stack_trace}</ScrollableTextArea>
            </ClickCopy>
          </LabelValue>
        )}
        <FullWidthDivider />

        <SectionHeader variant="h6">References</SectionHeader>
        <ExceptionReferences exception={exception} labelValueProps={LabelValueProps} />
        <FullWidthDivider />

        <SectionHeader variant="h6">Actions</SectionHeader>
        <List sx={{ ml: -2, pt: 0 }}>
          {exception.actions?.length
            ? exception.actions.map((action, index) => {
                return action.type === 'link' ? (
                  <DrawerLinkActionButton key={index} action={action} references={exception.references} />
                ) : (
                  <DrawerApiActionButton key={index} action={action} references={exception.references} />
                );
              })
            : null}
          {exception.status !== 'RESOLVED' && <ExceptionResolve listItem exception={exception} onResolutionSubmitted={refreshExceptions} />}
        </List>
      </Grid>
    </FormattedDrawer>
  );
};

export default ExceptionDrawer;

const ScrollableTextArea = styled('pre')`
  overflow: auto;
  max-height: 200px;
  white-space: pre-wrap;
`;

const FullWidthDivider = styled(Divider)`
  margin-top: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  width: 100%;
`;

const SectionHeader = styled(Typography)`
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  width: 100%;
  font-size: 1.15rem;
`;

const RefreshButton = styled(Refresh)`
  opacity: 0.6;
  margin-left: auto;
  transition: ease opacity 250ms;

  &.processing-exceptions {
    top: 0;
  }

  &:hover {
    opacity: 0.8;
    transition: ease opacity 250ms;
  }

  span {
    top: ${({ theme }) => theme.spacing(1)};
  }
`;

type FormattedDrawerProps = {
  popout: string;
  footer?: string;
};

const FormattedDrawer = styled(Drawer)<FormattedDrawerProps>`
  ${({ theme }) => convertStylesToStyled(theme.customOverrides.TaskDrawer)}

  .MuiDrawer-paper {
    width: 100%;
    transition: ease opacity 250ms;
    font-size: 14px;

    ${({ theme }) => theme.breakpoints.up('sm')} {
      max-width: ${({ popout }) => (popout === 'true' ? 'none' : '420px')};
    }
  }
`;

const ActionBar = styled(Grid)`
  padding: ${({ theme }) => theme.spacing(0, 2)};
  margin: ${({ theme }) => theme.spacing(1.5, 0, 1)};

  .MuiIconButton-root {
    opacity: 0.5;
    transition: ease opacity 250ms;

    &:hover {
      transition: ease opacity 250ms;
      opacity: 0.6;
    }
  }
`;
